"use client";

import React from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { useCompanyQuickBooksCustomer } from "@/features/companies/hooks/company-quickbooks-hooks";
import type { QuickBooksCustomerWithRealm } from "@/lib/types/quickbooks-unified";

interface CompanyQuickBooksCustomerConnectionsProps {
  companyId: string;
}

export const CompanyQuickBooksCustomerConnections: React.FC<
  CompanyQuickBooksCustomerConnectionsProps
> = ({ companyId }) => {
  const { data: qbData, isLoading, error } = useCompanyQuickBooksCustomer(companyId);

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">QuickBooks Connections</CardTitle>
          <CardDescription>Loading QuickBooks data...</CardDescription>
        </CardHeader>
      </Card>
    );
  }

  if (error || !qbData?.customerData) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">QuickBooks Connections</CardTitle>
          <CardDescription>No QuickBooks customers connected</CardDescription>
        </CardHeader>
      </Card>
    );
  }

  const customerData = (qbData.customerData || []) as QuickBooksCustomerWithRealm[];

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg">QuickBooks Connections ({customerData.length})</CardTitle>
        <CardDescription>Connected QuickBooks customers from multiple realms</CardDescription>
      </CardHeader>
      <CardContent>
        <div className="space-y-3">
          {customerData.map((customer, index) => (
            <div
              key={`${customer.Id}-${index}`}
              className="flex items-center justify-between p-3 border rounded-lg"
            >
              <div className="flex-1">
                <div className="font-medium">{customer.Name || "QuickBooks Customer"}</div>
                <div className="text-sm text-muted-foreground">
                  Customer ID: {customer.Id}
                  {customer.realmId && (
                    <span className="ml-2">
                      <Badge variant="outline" className="text-xs">
                        Realm: {customer.realmId}
                      </Badge>
                    </span>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
};
